/**
 * engine/garantias.ts — composição das garantias reais (FSF por unidade e terreno).
 * Cópia literal de fincast-viabilidade-v67.html, linhas 1405-1411, 1419 e 2868-2871.
 *
 * ⚠ RETORNO TRI-ESTADO de `terrenoGarFrac` e `terrenoGarM2`: `0` = terreno não entra;
 * `null` = "sem dados, caia para o próximo fallback"; número = valor. A cadeia de fallback
 * está em `calcBase` (linha 1459) e `calcEstatico` (linha 1486) e é DIFERENTE entre as
 * duas de propósito — não unificar.
 */

import {
  _ingressaValida, _saiuEstoqueDem, _vagaExcl, _vagasVinc, _vagasVincDet, baseDaVaga, baseDem, baseDoPacote, demTemDados,
} from './demanda';
import type {
  DetalheFSF, DetalheTerreno, Empreendimento, EstoqueDemanda, ForaTerreno, ItemFSF, ItemTerreno, LadoUnidades, Lev, Unidade,
} from './tipos';

/**
 * GARANTIA EM ESTOQUE pelo VALOR REAL da tabela EL-154 — 29/07/2026, Parte A do IGH.
 *
 * Substitui `contagem × preço médio` no IGH estático (EL-129/EL-093) quando a tabela de
 * demanda por unidade tem dados. O interruptor é `demTemDados` — o MESMO que o FSF usa,
 * de propósito: se a tabela alimenta o FSF, ela também alimenta o estoque.
 *
 *   estoque = Σ base das unidades que FICARAM
 *           + Σ base das vagas VINCULADAS a essas unidades
 *           + Σ base das vagas SOLTAS (não vinculadas a nenhuma unidade)
 *
 * ⚠ 08/10/2026 (EL-191): "base" era a AVALIAÇÃO. Passou a ser a base de cálculo — o menor
 * entre venda e avaliação, comparando o pacote unidade + vagas vinculadas, salvo avaliação
 * forçada (`baseDoPacote`); a vaga solta pelo menor automático (`baseDaVaga`).
 *
 * Regras que vêm da calibração de 28/07/2026:
 * - Quem sai é decidido por `_saiuEstoqueDem` (EL-057 ∪ EL-114) — a régua NÃO muda, só o valor.
 * - Vincular uma vaga a uma unidade é "o jeito de dizer" que a vaga foi vendida com ela:
 *   por isso a vaga vinculada acompanha o destino da unidade (fica se ela fica, sai se ela sai).
 * - Toda vaga avulsa não vendida (= não vinculada) é estoque.
 * - `_vagaExcl` (permutada **E** informada à Caixa) sai de tudo — vinculada ou solta.
 *   Uma vaga permutada e NÃO informada continua sendo estoque ("é estoque enquanto não for
 *   informada"), o que `_vagaExcl` já garante por exigir as duas condições.
 *
 * `null` = tabela sem dados; o chamador mantém `contagem × preço médio` (idêntico a antes).
 *
 * ⚠ `unidades` conta só UNIDADES (vaga avulsa não é unidade) e é a contagem das LINHAS da
 * tabela que ficaram — pode divergir de `b.unidades − EL-057 − EL-114` quando a tabela lista
 * menos linhas do que o campo "Unidades totais". No produto o campo é derivado da tabela
 * (lote de 27/07/2026), então os dois coincidem.
 */
export function estoqueDemReal(lev: Lev): EstoqueDemanda | null {
  if (!demTemDados(lev)) return null;
  const uns = Array.isArray(lev.unidadesDem) ? lev.unidadesDem : [];
  const vgs = Array.isArray(lev.vagasDem) ? lev.vagasDem : [];
  /* vaga citada por QUALQUER unidade não é solta — mesmo que a unidade dona tenha saído */
  const vinculadas = new Set<string>();
  uns.forEach(u => { (Array.isArray(u.vagas) ? u.vagas : []).forEach(id => vinculadas.add(id)); });
  let valorUnidades = 0, valorVagasVinculadas = 0, valorVagasSoltas = 0, unidades = 0, pelaVenda = 0, forcadas = 0, vagasPelaVenda = 0;
  uns.forEach(u => {
    if (_saiuEstoqueDem(lev, u)) return;
    unidades++;
    /* A base do PACOTE (unidade + vagas vinculadas não excluídas) já vem repartida entre a
       unidade e as vagas. As parcelas saem separadas porque a memória de cálculo (EL-184)
       mostra as três — e mostrar é diferente de RECALCULAR: a regra vive só em `baseDoPacote`. */
    const bc = baseDem(lev, u);
    valorUnidades += bc.unidade;
    valorVagasVinculadas += bc.vagas;
    if (bc.origem === 'venda') pelaVenda++;
    if (bc.origem === 'forcada') forcadas++;
  });
  vgs.forEach(v => {
    if (_vagaExcl(v) || vinculadas.has(v.id)) return;
    const bv = baseDaVaga(v);
    valorVagasSoltas += bv;
    if (bv < (v.avaliacao || 0)) vagasPelaVenda++;
  });
  return {
    valor: valorUnidades + valorVagasVinculadas + valorVagasSoltas,
    unidades, valorUnidades, valorVagasVinculadas, valorVagasSoltas, pelaVenda, forcadas, vagasPelaVenda,
  };
}

/**
 * O REPASSE DE UMA UNIDADE — a fórmula da Caixa, e a ÚNICA do motor.
 *
 *     repasse = máx(0, valor da unidade × % financiado − fração ideal do terreno)
 *
 * É o "Preço Médio de Repasse" da planilha MO 3041 (`G82`), conferido no centavo contra a
 * operação VERUS em `tests/caixa-mo3041.test.ts`. O clamp em zero é da planilha
 * (`IF(...<0, 0, ...)`) e não é detalhe: sem ele, terreno caro com financiamento baixo
 * geraria repasse NEGATIVO, inflando o PJ.
 *
 * ⚠ EXISTE PARA SER O ÚNICO LUGAR. Até 31/07/2026 o motor tinha TRÊS fórmulas de FSF
 * discordando entre si — a estática (sem desconto do terreno), a do fluxo mensal
 * (com desconto, mas sem o percentual de financiamento) e a das barras de garantia
 * (com percentual, sem desconto). Cada uma alimentava uma tela, e as três contavam
 * histórias diferentes sobre a mesma operação. Quem precisar do repasse chama daqui.
 *
 * @param valor       a base de cálculo da unidade (`baseDoPacote`) — ou o preço médio, no caminho agregado
 * @param pctFin      percentual financiado, de 0 a 100
 * @param fracaoTerreno  a parte do terreno que cabe a esta unidade, em reais
 */
export function repasseDaUnidade(valor: number, pctFin: number, fracaoTerreno: number): number {
  return Math.max(0, valor * ((pctFin || 0) / 100) - (fracaoTerreno || 0));
}

/**
 * O "PJ LIMITE" INFORMADO (EL-192, 09/10/2026) — um teto ADICIONAL do PJ, opcional.
 *
 * Pedido: "se neste campo for inserido um valor inferior às demais regras, valerá o valor
 * inserido neste campo". Por isso ele é só mais um termo do `Math.min` das travas — o PJ
 * continua sendo `máx(0, mín(o que falta, teto da linha de crédito, teto normativo, ESTE))`.
 *
 * Mora no CADASTRO, como o "LGC disponível" — e o cadastro é de CADA CENÁRIO desde a 0032
 * (12/10/2026; até ali era do empreendimento e valia para todos os cenários dele). O motor não
 * muda: lê `b.pjLimite` do empreendimento que recebe.
 *
 *   número finito > 0           → é o teto
 *   ausente, null, 0, negativo,
 *   NaN, ±Infinity, não-número  → `Infinity` (sem limite)
 *
 * Zero é "sem limite", não "PJ zero": os campos de valor da tela mostram zero como vazio,
 * e é assim que o usuário apaga o campo.
 *
 * ⚠ `Infinity` é o que garante o campo VAZIO idêntico ao de antes, byte a byte: o
 * `Math.min` devolve exatamente o mesmo número com um argumento a mais que é `Infinity`.
 *
 * ⚠ EXISTE PARA SER O ÚNICO LUGAR, como `repasseDaUnidade`: a trava é aplicada em TRÊS
 * cópias da mesma regra — `calcBase` (fluxo e otimizador), `calcEstatico` (tela de
 * Premissas) e `_barraDoMes` (barras do EL-110 e o mínimo do IGH do EL-078). As três leem
 * daqui. NÃO é exportada pelo `engine/index.ts`: a tela não a usa (ela mostra o efeito
 * comparando dois `calcEstatico`, com e sem a chave).
 */
export function tetoInformadoPJ(b: Empreendimento): number {
  /* `unknown`: vem do rascunho/jsonb e do golden, não só do tipo — a guarda é de verdade */
  const v: unknown = b.pjLimite;
  return (typeof v === 'number' && Number.isFinite(v) && v > 0) ? v : Infinity;
}

/**
 * FSF — o que o comprador pessoa física assume, por unidade.
 *
 * v67 linha 1419 (v54 item 4 + v57), **CORRIGIDA em 31/07/2026**.
 *
 * FSF = Σ máx(0, base de cálculo × % efetivo − fração ideal do terreno da unidade)
 * sobre as unidades VÁLIDAS para demanda.
 *
 * ⚠ 08/10/2026 (EL-191): a base era a "avaliação efetiva" (`_avalEfet`). Passou a ser
 * `baseDem` — o menor entre venda e avaliação do pacote, salvo avaliação forçada. % efetivo = `u.percFin` se > 0, senão
 * `lev.pctFin` (0 é sentinela de "usa o padrão").
 *
 * ⚠ O DESCONTO DO TERRENO É A CORREÇÃO, e ele veio da planilha da própria Caixa
 * (MO 3041 01 09, aba "AP SBPE e RECLIV MAP"), que calcula:
 *
 *     Preço Médio de Repasse = (preço médio × % financiado) − Fração Ideal do Terreno
 *     Total de Recursos Propostos = unidades repassadas × Preço Médio de Repasse
 *
 * Conferido no caso VERUS: 19 × (586.725,81 × 70% − 31.387,10) = R$ 7.207.098,39, que é
 * exatamente o "Total de Recursos Propostos" da planilha. Sem o desconto dava
 * R$ 7.803.453 — R$ 596.355 a mais, e como o PJ é o que SOBRA depois das garantias, o
 * simulador prometia menos empréstimo do que a Caixa exige.
 *
 * ⚠ O desconto é INCONDICIONAL: no caso conferido, "Aporte da Fração Ideal do Terreno"
 * estava como "Não" e a planilha descontou assim mesmo. Não tem relação com o terreno
 * entrar ou não como garantia — é que o repasse não cobre a parte do terreno.
 *
 * ⚠ CLAMP EM ZERO POR UNIDADE, como na planilha (`IF(...<0, 0, ...)`): unidade cujo
 * financiamento não cobre nem a fração do terreno contribui com zero, não com negativo.
 *
 * A fração do terreno por unidade sai da FRAÇÃO IDEAL da tabela quando ela existe
 * (mais preciso que a média, e é o motivo de a tabela existir); sem fração declarada,
 * cai na divisão uniforme `valorTerreno ÷ unidades`, que é o que a Caixa faz.
 */
export function fsfPorUnidade(b: Empreendimento, lev: Lev): number {
  return fsfDetalhe(b, lev).total;
}

/**
 * O FSF por unidade, LINHA A LINHA — 08/10/2026, para a memória de cálculo (EL-184).
 *
 * É o corpo do `fsfPorUnidade`, devolvendo cada parcela em vez de só a soma. Existe para
 * a memória MOSTRAR o que o motor fez sem recalcular por fora: até aqui ela refazia
 * `avaliação × %` sozinha e ficou para trás quando o desconto do terreno entrou (31/07).
 */
export function fsfDetalhe(b: Empreendimento, lev: Lev): DetalheFSF {
  /* lote 2 de 09/10/2026 (item 16): o corpo virou `fsfDetalheDe`, que a barra do Fluxo também
     usa com o lado do Detalhamento. Aqui: o lado da Demanda e o portão `_ingressaValida`. As
     contas e a ordem das somas são as de antes — provado byte a byte no golden. */
  return fsfDetalheDe(b, lev, ladoDemanda(lev), u => _ingressaValida(lev, u));
}

/**
 * O lado "Demanda por unidade" (EL-154): `unidadesDem`, `vagasDem`, o vínculo por `_vagasVinc`
 * e a base por `baseDem`. É o lado da tela de Premissas.
 */
export function ladoDemanda(lev: Lev): LadoUnidades {
  return {
    unidades: lev.unidadesDem || [],
    vagas: Array.isArray(lev.vagasDem) ? lev.vagasDem : [],
    vinculadas: u => _vagasVinc(lev, u),
    base: u => baseDem(lev, u),
  };
}

/**
 * O lado do Detalhamento (EL-043/EL-147): `unidadesDet`, `vagasDet`, o vínculo resolvido
 * contra `vagasDet` (`_vagasVincDet`, a MESMA resolução da base) e a base do pacote. É o
 * lado das barras do Fluxo — lote 2 de 09/10/2026, item 16.
 *
 * ⚠ A BASE é a da tela de Premissas (`baseDoPacote` SEM o "avaliação senão venda"): avaliação
 * vazia vale ZERO, como no EL-154 — o invariante "a base nunca passa da avaliação" do EL-191.
 * Revisão do lote 2 (10/10/2026): com `baseDet` (que no EL-147 lê a unidade sem avaliação pela
 * venda), espelhar uma unidade vendida financiada SEM "Valor de avaliação" fazia a barra do mês do
 * Cumprimento divergir da Premissas — exatamente a reclamação do item 16, e contra a decisão
 * "Unidade a unidade" (uma soma só para as duas telas). O "avaliação senão venda" do `baseDet`
 * continua só onde foi decidido em 08/10/2026: o estoque e o FSF a receber do `calcIGH`.
 *
 * ⚠ A coluna `m2` deste lado é lida como FRAÇÃO IDEAL, como no da Demanda: na tela atual as
 * duas tabelas têm a mesma coluna "Fração Ideal" (e o "Espelhar" copia uma na outra). Só o
 * `terrenoGarM2`, herança da v39, ainda a lê como metragem.
 */
export function ladoDetalhamento(lev: Lev): LadoUnidades {
  return {
    unidades: Array.isArray(lev.unidadesDet) ? lev.unidadesDet : [],
    vagas: Array.isArray(lev.vagasDet) ? lev.vagasDet : [],
    vinculadas: u => _vagasVincDet(lev, u),
    base: u => baseDoPacote(u, _vagasVincDet(lev, u)),
  };
}

/**
 * A PARTE DO TERRENO QUE CABE A UMA UNIDADE — a MESMA no desconto do FSF e no terreno em
 * garantia (lote 2 de 09/10/2026: "uma regra só para os dois").
 *
 *   com fração na tabela (Σ > 0):  valorTerreno × fração da unidade ÷ Σ frações de tudo
 *   sem fração nenhuma (Σ = 0):    valorTerreno ÷ unidades totais (a divisão igual da Caixa)
 *
 * Com Σ > 0, a unidade sem fração própria (nem de vaga vinculada) fica com ZERO — nos dois
 * usos, porque os dois chamam daqui.
 */
export function terrenoDaUnidade(valorTerreno: number, unidades: number, frac: number, totalFrac: number): number {
  return totalFrac > 0 ? valorTerreno * (frac / totalFrac) : (unidades ? valorTerreno / unidades : 0);
}

/**
 * O FSF UNIDADE A UNIDADE, para QUALQUER lado da tabela — lote 2 de 09/10/2026, item 16.
 *
 * Pedido literal: "Aparentemente, mesmo espelhando dados em EL-154 e EL-043, há uma
 * divergência nos resultados." A divergência era de REGRA: a tela de Premissas somava
 * unidade a unidade e a barra do Fluxo usava a média (preço médio × % padrão − fração
 * média). Decisão: "Unidade a unidade (Recomendado)" — e com UMA soma por unidade só, para
 * as duas telas: esta. O `fsfDetalhe` (Premissas) é ela com o lado da Demanda e o portão
 * `_ingressaValida`; a barra (`_barraDoMes`) é ela com o lado do Detalhamento e o portão
 * "vendida financiada até o mês".
 *
 * Por unidade que passa no portão `entra`:
 *   % = "% financ." da linha se > 0, senão o padrão das premissas (`lev.pctFin`);
 *   fração = "Fração Ideal" da unidade + a das vagas vinculadas (`lado.vinculadas`);
 *   terreno = `terrenoDaUnidade` (fração ÷ Σ frações do lado; sem fração, divisão igual);
 *   repasse = `repasseDaUnidade(base, %, terreno)`.
 *
 * ⚠ A ORDEM DAS SOMAS é a do corpo antigo do `fsfDetalhe` (unidades em ordem, divisor =
 * Σ unidades + Σ vagas): mudar a ordem muda o último bit e reprova o golden.
 */
export function fsfDetalheDe(
  b: Empreendimento, lev: Lev, lado: LadoUnidades, entra: (u: Unidade, i: number) => boolean,
): DetalheFSF {
  const padrao = lev.pctFin || 0;
  const uns = lado.unidades, vgs = lado.vagas;
  /* mesma base do `terrenoGarFrac`: fração ideal TOTAL (unidades + vagas) */
  const totalFrac = uns.reduce((s, u) => s + (u.m2 || 0), 0)
    + vgs.reduce((s, v) => s + (v.fracao || 0), 0);
  const valorTerreno = b.valorTerreno || 0;
  const itens: ItemFSF[] = [];
  let total = 0, fracEntram = 0, terrenoPartes = 0;
  uns.forEach((u, i) => {
    if (!entra(u, i)) return;
    /* `u.percFin` ausente compara como `undefined > 0` = false → cai no padrão (idêntico ao protótipo) */
    const pctProprio = (u.percFin as number) > 0;
    const pct = pctProprio ? (u.percFin as number) : padrao;
    /* = `_fracEfet` no lado da Demanda: a unidade + as vagas vinculadas não excluídas */
    const fracVagas = lado.vinculadas(u).reduce((s, v) => s + (v.fracao || 0), 0);
    const frac = (u.m2 || 0) + fracVagas;
    const terreno = terrenoDaUnidade(valorTerreno, b.unidades, frac, totalFrac);
    const base = lado.base(u);
    const repasse = repasseDaUnidade(base.valor, pct, terreno);
    total += repasse;
    fracEntram = fracEntram + frac;
    terrenoPartes += terreno;
    itens.push({ i, nome: u.nome || '', base, pct, pctProprio, fracUnidade: u.m2 || 0, fracVagas, frac, terreno, repasse });
  });
  /* com fração, o total do terreno é a conta do `terrenoGarFrac` (uma divisão só, não a soma
     das partes — é o que o golden confere bit a bit); sem fração, a soma das partes iguais */
  const terreno = totalFrac > 0 ? valorTerreno * (fracEntram / totalFrac) : terrenoPartes;
  return { itens, total, totalFrac, fracEntram, terreno };
}

/**
 * v67 linhas 2868-2871 (v54 item 11, corrigida na v58).
 * Terreno em garantia pela FRAÇÃO IDEAL da tabela EL-154.
 *
 * - `0`    → terreno não é garantia.
 * - `null` → EL-154 sem dados; o chamador cai no fallback (m² e, depois, uniforme).
 * - v58: normaliza pela Σ TOTAL (unidades + vagas) do lev recebido, e não pela soma
 *   absoluta — é o que impede o estouro quando a soma das frações ≠ 1. Resultado ≤
 *   `valorTerreno`, idêntico ao anterior quando Σ = 1, e 0 quando Σ = 0 (não divide por zero).
 */
export function terrenoGarFrac(b: Empreendimento, lev: Lev): number | null {
  if (!lev.terrenoGarantia) return 0;
  /* 09/10/2026 (item 28): o corpo virou `terrenoDetalhe`, que devolve as parcelas — o total
     sai das MESMAS somas, na MESMA ordem (provado contra o corpo antigo em todos os casos do
     golden, em tests/terreno-detalhe.test.ts) */
  const d = terrenoDetalhe(b, lev);
  return d ? d.total : null;
}

/**
 * O terreno em garantia pela Fração Ideal, LINHA A LINHA — lote de 09/10/2026, item 28.
 *
 * Pedido literal: "O item terreno em EL-124 não está com uma memória de cálculo
 * suficientemente detalhada. Ela não está listando as unidades e o cálculo da fração de
 * terreno associadas a essas unidades que compõem o valor total."
 *
 * É o corpo antigo do `terrenoGarFrac` devolvendo cada parcela em vez de só a soma — o mesmo
 * movimento que `fsfPorUnidade` → `fsfDetalhe`. A memória de cálculo LÊ daqui; não refaz a
 * conta. ⚠ As somas que formam `total` têm de continuar na ordem do corpo antigo (unidades
 * válidas em ordem, a partir de 0; divisor = Σ unidades + Σ vagas): mudar a ordem muda o
 * último bit e reprova o golden.
 *
 * Não olha "Terreno como garantia?" (quem liga e desliga é o `terrenoGarFrac`), e devolve
 * `null` quando a tabela "Demanda por unidade" não tem dados — o mesmo `null` que manda o
 * chamador para os caminhos seguintes (m² e divisão igual).
 *
 * ⚠ LOTE 2 DE 09/10/2026 — TERRENO SEM FRAÇÃO IDEAL ("Corrigir no lote 2 (Recomendado)"):
 * com a tabela preenchida e NENHUMA "Fração Ideal" (Σ = 0), o terreno em garantia dava R$ 0
 * enquanto o FSF descontava de cada unidade o terreno dividido igualmente. Agora as linhas
 * SAEM do `fsfDetalheDe` — a mesma soma, com o mesmo portão `_ingressaValida` —, então a
 * parte de cada unidade é, por construção, a que o FSF desconta dela: com fração, a fração;
 * sem fração nenhuma, `valorTerreno ÷ unidades`. Uma regra só para os dois. Com Σ > 0 nada
 * muda (a unidade sem fração própria fica com zero nos dois, como antes).
 */
export function terrenoDetalhe(b: Empreendimento, lev: Lev): DetalheTerreno | null {
  if (!demTemDados(lev)) return null;
  const lado = ladoDemanda(lev);
  const d = lado.unidades, vgs = lado.vagas;
  const valorTerreno = b.valorTerreno || 0;
  /* v58: fração ideal TOTAL do empreendimento (todas as unidades + todas as vagas) — as duas
     parcelas saem separadas para a memória; o divisor é o do `fsfDetalheDe` (a mesma soma) */
  const totalFracUnidades = d.reduce((s, u) => s + (u.m2 || 0), 0);
  const totalFracVagas = vgs.reduce((s, v) => s + (v.fracao || 0), 0);
  const fora: ForaTerreno = { semIngressa: 0, naoFinanciada: 0, permutada: 0, dataFora: 0 };
  d.forEach(u => {
    if (_ingressaValida(lev, u)) return;
    /* o motivo pela CAUSA (revisão do lote 1, 10/10/2026): na tela, marcar "Permutada/quitada?"
       desmarca "Financiada?" e "Ingressa…", e desmarcar "Financiada?" desmarca "Ingressa…". Na ordem
       do `_ingressaValida`, a permutada marcada pela tela caía em "sem Ingressa" — o efeito, não o
       que a pessoa marcou. Só exibição: não mexe em `fracEntram` nem em `total`. */
    if (!u) fora.semIngressa++;
    else if (u.permutada) fora.permutada++;
    else if (!u.financiada) fora.naoFinanciada++;
    else if (!u.ingressaDemanda) fora.semIngressa++;
    else fora.dataFora++;
  });
  /* v57: só unidades válidas p/ demanda (ingressa + financiada + data ≤ EL-065) */
  const f = fsfDetalheDe(b, lev, lado, u => _ingressaValida(lev, u));
  const totalFrac = f.totalFrac;
  const semFracao = !(totalFrac > 0);
  const U = b.unidades || 0;
  const itens: ItemTerreno[] = f.itens.map(x => ({
    i: x.i, nome: x.nome, fracUnidade: x.fracUnidade, fracVagas: x.fracVagas, frac: x.frac,
    parte: semFracao ? (U ? 1 / U : 0) : x.frac / totalFrac,
    terreno: x.terreno,                                    /* a parte que o FSF desconta desta unidade */
  }));
  return {
    itens, fracEntram: f.fracEntram,
    parteEntram: semFracao ? (U ? itens.length / U : 0) : f.fracEntram / totalFrac,
    totalFracUnidades, totalFracVagas, totalFrac,
    nUnidades: d.length, nVagas: vgs.length, valorTerreno, total: f.terreno, fora,
    semFracao, porUnidadeSemFracao: U ? valorTerreno / U : 0,
  };
}

/**
 * v67 linhas 1405-1411 (item 22 da v39).
 * Terreno em garantia ponderado pelo m² privativo das unidades do lado `det` (EL-147).
 *
 * ⚠ Lê `lev.unidadesDet` — tabela DIFERENTE da `lev.unidadesDem` usada por
 * `terrenoGarFrac`. São duas bases paralelas de unidades.
 * `null` = sem m² → o chamador mantém a fração uniforme.
 * O gate aqui é `financiada && !permutada` (NÃO passa por `_ingressaValida`).
 */
export function terrenoGarM2(b: Empreendimento, lev: Lev): number | null {
  if (!lev.terrenoGarantia) return 0;
  const det = lev.unidadesDet || [], totM2 = det.reduce((s, u) => s + (u.m2 || 0), 0);
  if (totM2 <= 0) return null;   /* sem m² → mantém a fração uniforme */
  const m2Fin = det.filter(u => u.financiada && !u.permutada).reduce((s, u) => s + (u.m2 || 0), 0);
  return (b.valorTerreno || 0) * (m2Fin / totM2);
}
